import React, { useState, useEffect } from 'react';
import { IconStar, IconPlus, IconTrash, IconCheck, IconEdit } from './Icons';
import { saveField, loadUserData } from '../supabase';
import { ARENAS } from '../constants';

// ─────────────────────────────────────────────────────────────────────────
// Foundations — four one-time reflective exercises, saveable and editable.
// Each exercise renders as an expandable card in a scrollable feed.
// Data shape on Supabase: user_data.foundations = { core: {...}, longings: {...}, arenas: {...}, phrases: [...] }
// ─────────────────────────────────────────────────────────────────────────

// Default empty state for each exercise — used for new users or resets.
const DEFAULTS = {
  core: { qualities: '', expressions: '', world: '', sentence: '' },
  longings: { items: '' },
  arenas: ARENAS.reduce((acc, a) => ({ ...acc, [a.key]: '' }), {}),
  phrases: [],
};

export default function Foundations({ userId }) {
  const [foundations, setFoundations] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(null); // which exercise card is open
  const [editing, setEditing] = useState(null); // which exercise is in edit mode

  useEffect(() => {
    async function load() {
      if (userId) {
        const data = await loadUserData(userId);
        const saved = data?.foundations || {};
        setFoundations({
          core: { ...DEFAULTS.core, ...(saved.core || {}) },
          longings: { ...DEFAULTS.longings, ...(saved.longings || {}) },
          arenas: { ...DEFAULTS.arenas, ...(saved.arenas || {}) },
          phrases: saved.phrases || [],
        });
      } else {
        try {
          const local = JSON.parse(localStorage.getItem('bhn_foundations') || 'null');
          if (local) setFoundations({ ...DEFAULTS, ...local });
        } catch {}
      }
      setLoaded(true);
    }
    load();
  }, [userId]);

  async function persist(updated) {
    setFoundations(updated);
    if (userId) await saveField(userId, 'foundations', updated);
    else localStorage.setItem('bhn_foundations', JSON.stringify(updated));
  }

  // Helpers for saving individual exercises.
  async function saveCore(core) { await persist({ ...foundations, core }); setEditing(null); }
  async function saveLongings(longings) { await persist({ ...foundations, longings }); setEditing(null); }
  async function saveArenas(arenas) { await persist({ ...foundations, arenas }); setEditing(null); }
  async function savePhrases(phrases) { await persist({ ...foundations, phrases }); }

  // Each exercise is "completed" if it has meaningful content saved.
  const completed = {
    core: !!(foundations.core.sentence && foundations.core.sentence.trim()),
    longings: !!(foundations.longings.items && foundations.longings.items.trim()),
    arenas: Object.values(foundations.arenas).some(v => v && v.trim()),
    phrases: foundations.phrases.length > 0,
  };

  const EXERCISES = [
    { key: 'core', title: 'Your Core', subtitle: 'A one-sentence purpose statement', time: '10 min' },
    { key: 'longings', title: '30 Longings', subtitle: 'What you want to have, do, and feel', time: '15 min' },
    { key: 'arenas', title: 'The Seven Arenas', subtitle: 'A vision across seven areas of life', time: '20 min' },
    { key: 'phrases', title: 'Your Phrases', subtitle: 'Affirmations written in your own voice', time: '5 min' },
  ];

  if (!loaded) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, width: '100%' }}>
        <div style={{ fontSize: 13, color: '#A09888' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', height: '100%' }}>
      <div className="top-bar">
        <div className="brand-small">Foundations</div>
        <div className="screen-title">Your Groundwork</div>
      </div>
      <div className="screen-body" style={{ width: '100%' }}>
        <div style={{ fontSize: 13, color: '#777', lineHeight: 1.7, marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid #F5F3F0' }}>
          Four reflections that ground the rest of your practice. Do them when you have quiet time. Come back and edit them whenever you want.
        </div>

        {EXERCISES.map(ex => {
          const isOpen = expanded === ex.key;
          const isEditing = editing === ex.key;
          const done = completed[ex.key];
          return (
            <div key={ex.key} style={{ borderBottom: '1px solid #F5F3F0', paddingBottom: 20, marginBottom: 20 }}>
              {/* Card header — always visible */}
              <div onClick={() => setExpanded(isOpen ? null : ex.key)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                  <div style={{ width: 26, height: 26, borderRadius: '50%', background: done ? '#3A6A2A' : '#F5F3F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {done ? <IconCheck size={14} color="white" /> : <IconStar size={13} color="#A09888" />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#111', letterSpacing: '-.2px' }}>{ex.title}</div>
                    <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{ex.subtitle}</div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: '#C8C4BE', letterSpacing: '.08em', fontWeight: 600, textTransform: 'uppercase', flexShrink: 0, marginLeft: 8 }}>
                  {isOpen ? '▾' : '▸'} {ex.time}
                </div>
              </div>

              {/* Expanded body */}
              {isOpen && (
                <div style={{ marginTop: 18, paddingLeft: 2 }}>
                  {ex.key === 'core' && (
                    <CoreExercise data={foundations.core} done={done && !isEditing} editing={isEditing} onEdit={() => setEditing('core')} onSave={saveCore} onCancel={() => setEditing(null)} />
                  )}
                  {ex.key === 'longings' && (
                    <LongingsExercise data={foundations.longings} done={done && !isEditing} editing={isEditing} onEdit={() => setEditing('longings')} onSave={saveLongings} onCancel={() => setEditing(null)} />
                  )}
                  {ex.key === 'arenas' && (
                    <ArenasExercise data={foundations.arenas} done={done && !isEditing} editing={isEditing} onEdit={() => setEditing('arenas')} onSave={saveArenas} onCancel={() => setEditing(null)} />
                  )}
                  {ex.key === 'phrases' && (
                    <PhrasesExercise data={foundations.phrases} onSave={savePhrases} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 1. YOUR CORE — a 4-part purpose sentence
// ─────────────────────────────────────────────────────────────────────────
function CoreExercise({ data, done, editing, onEdit, onSave, onCancel }) {
  const [form, setForm] = useState(data);
  useEffect(() => { setForm(data); }, [data, editing]);

  if (done) {
    return (
      <div>
        <div style={{ fontSize: 14, color: '#111', fontStyle: 'italic', lineHeight: 1.7, padding: '16px 18px', background: '#FAFAF8', borderLeft: '3px solid #3A6A2A', borderRadius: '0 10px 10px 0', marginBottom: 12 }}>
          "{data.sentence}"
        </div>
        {(data.qualities || data.expressions || data.world) && (
          <details style={{ marginBottom: 12 }}>
            <summary style={{ fontSize: 11, color: '#A09888', cursor: 'pointer', fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase' }}>See my answers</summary>
            <div style={{ marginTop: 10, fontSize: 12, color: '#555', lineHeight: 1.7 }}>
              {data.qualities && <div style={{ marginBottom: 6 }}><strong style={{ color: '#777' }}>Qualities:</strong> {data.qualities}</div>}
              {data.expressions && <div style={{ marginBottom: 6 }}><strong style={{ color: '#777' }}>Expressions:</strong> {data.expressions}</div>}
              {data.world && <div><strong style={{ color: '#777' }}>World I want:</strong> {data.world}</div>}
            </div>
          </details>
        )}
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 0 }} onClick={onEdit}>
          <IconEdit size={13} color="#333" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#777', lineHeight: 1.7, marginBottom: 16 }}>
        A short sentence that names your essence and what you are here to offer. Four small steps — take your time.
      </div>
      <Field label="Two qualities people feel when you are at your best"
        hint="Be specific — not 'nice,' but patient, curious, direct, warm."
        value={form.qualities} onChange={v => setForm(p => ({ ...p, qualities: v }))} />
      <Field label="Two ways those qualities show up with others"
        hint="Teaching. Listening. Building. Encouraging. Organizing."
        value={form.expressions} onChange={v => setForm(p => ({ ...p, expressions: v }))} />
      <Field label="A world you want to help create"
        hint="What would be different about the world five years from now if you had your way?"
        value={form.world} onChange={v => setForm(p => ({ ...p, world: v }))} rows={3} />
      <Field label="Put it in one sentence"
        hint="I use my [qualities] to [express them] so that [the world you want]."
        value={form.sentence} onChange={v => setForm(p => ({ ...p, sentence: v }))} rows={3} />
      <ActionRow onSave={() => onSave(form)} onCancel={onCancel} saveLabel="Save my core" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 2. 30 LONGINGS — a freeform list
// ─────────────────────────────────────────────────────────────────────────
function LongingsExercise({ data, done, editing, onEdit, onSave, onCancel }) {
  const [text, setText] = useState(data.items || '');
  useEffect(() => { setText(data.items || ''); }, [data, editing]);

  const lines = text.split('\n').filter(l => l.trim());

  if (done) {
    return (
      <div>
        <div style={{ fontSize: 11, color: '#A09888', letterSpacing: '.08em', fontWeight: 600, textTransform: 'uppercase', marginBottom: 10 }}>
          {lines.length} {lines.length === 1 ? 'longing' : 'longings'} captured
        </div>
        <div style={{ background: '#FAFAF8', borderRadius: 10, padding: 14, marginBottom: 12 }}>
          {lines.map((l, i) => (
            <div key={i} style={{ fontSize: 13, color: '#333', lineHeight: 1.7, display: 'flex', gap: 10 }}>
              <span style={{ color: '#C8C4BE', minWidth: 18 }}>{i + 1}.</span>
              <span>{l}</span>
            </div>
          ))}
        </div>
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 0 }} onClick={onEdit}>
          <IconEdit size={13} color="#333" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#777', lineHeight: 1.7, marginBottom: 14 }}>
        Set a timer for 15 minutes. Write 30 things you long for — things you want to <em>have</em>, things you want to <em>do</em>, and ways you want to <em>feel</em>. Mix them up. Do not edit. When the timer ends, read them back and notice which ones surprised you.
      </div>
      <div className="input-label">Your longings — one per line</div>
      <textarea className="j-textarea"
        placeholder={"1. ...\n2. ...\n3. ...\netc."}
        value={text} onChange={e => setText(e.target.value)} rows={12}
        style={{ width: '100%' }} />
      <div style={{ fontSize: 11, color: '#A09888', marginTop: 6, marginBottom: 14 }}>
        {lines.length} written so far
      </div>
      <ActionRow onSave={() => onSave({ items: text })} onCancel={onCancel} saveLabel="Save my longings" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3. THE SEVEN ARENAS — one vision per life area
// ─────────────────────────────────────────────────────────────────────────
function ArenasExercise({ data, done, editing, onEdit, onSave, onCancel }) {
  const [form, setForm] = useState(data);
  useEffect(() => { setForm(data); }, [data, editing]);

  if (done) {
    return (
      <div>
        {ARENAS.map(a => (
          <div key={a.key} style={{ marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #F5F3F0' }}>
            <div style={{ fontSize: 11, color: '#3A6A2A', letterSpacing: '.08em', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{a.label}</div>
            <div style={{ fontSize: 13, color: data[a.key] ? '#333' : '#C8C4BE', lineHeight: 1.7, fontStyle: data[a.key] ? 'normal' : 'italic' }}>
              {data[a.key] || 'Not yet written.'}
            </div>
          </div>
        ))}
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 8, marginBottom: 0 }} onClick={onEdit}>
          <IconEdit size={13} color="#333" /> Edit
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#777', lineHeight: 1.7, marginBottom: 16 }}>
        Three years from now, at your best, what does each area of your life look like? Be specific — numbers, places, people, details. You can do it all at once, or come back one arena at a time.
      </div>
      {ARENAS.map(a => (
        <div key={a.key} style={{ marginBottom: 18 }}>
          <div className="input-label">{a.label}</div>
          <div style={{ fontSize: 11, color: '#A09888', marginBottom: 6, lineHeight: 1.6 }}>{a.hint}</div>
          <textarea className="j-textarea"
            placeholder="Three years from now..."
            value={form[a.key] || ''} onChange={e => setForm(p => ({ ...p, [a.key]: e.target.value }))} rows={3}
            style={{ width: '100%' }} />
        </div>
      ))}
      <ActionRow onSave={() => onSave(form)} onCancel={onCancel} saveLabel="Save my arenas" />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 4. YOUR PHRASES — user-created affirmations
// ─────────────────────────────────────────────────────────────────────────
function PhrasesExercise({ data, onSave }) {
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState('');

  function add() {
    if (!newText.trim()) return;
    const updated = [...data, { id: Date.now(), text: newText.trim() }];
    onSave(updated);
    setNewText('');
    setAdding(false);
  }
  function remove(id) { onSave(data.filter(p => p.id !== id)); }

  return (
    <div>
      <div style={{ fontSize: 12, color: '#777', lineHeight: 1.7, marginBottom: 14 }}>
        Short phrases written in your own voice. Guidelines that usually help:
      </div>
      <ul style={{ fontSize: 12, color: '#555', lineHeight: 1.9, paddingLeft: 20, marginBottom: 18 }}>
        <li>Start with "I am" or "I'm becoming"</li>
        <li>Present tense. Positive (what you want, not what you don't)</li>
        <li>Short — one breath long</li>
        <li>Include a feeling — "I am energized and clear" beats "I am productive"</li>
      </ul>

      {data.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          {data.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: '#FAFAF8', borderLeft: '3px solid #3A6A2A', borderRadius: '0 10px 10px 0', marginBottom: 8 }}>
              <div style={{ fontSize: 13, color: '#111', fontStyle: 'italic', lineHeight: 1.6, flex: 1 }}>"{p.text}"</div>
              <button onClick={() => remove(p.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: .4, padding: 2, flexShrink: 0 }}>
                <IconTrash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <div>
          <textarea className="j-textarea"
            placeholder="I am..."
            value={newText} onChange={e => setNewText(e.target.value)} rows={2}
            style={{ width: '100%', marginBottom: 10 }} autoFocus />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-primary" style={{ flex: 1, marginBottom: 0 }} onClick={add}>Add phrase</button>
            <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={() => { setAdding(false); setNewText(''); }}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginBottom: 0 }} onClick={() => setAdding(true)}>
          <IconPlus size={13} color="#333" /> Add a phrase
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Shared sub-components
// ─────────────────────────────────────────────────────────────────────────
function Field({ label, hint, value, onChange, rows = 2 }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="input-label">{label}</div>
      {hint && <div style={{ fontSize: 11, color: '#A09888', marginBottom: 6, lineHeight: 1.6 }}>{hint}</div>}
      <textarea className="j-textarea"
        value={value || ''} onChange={e => onChange(e.target.value)} rows={rows}
        style={{ width: '100%' }} />
    </div>
  );
}

function ActionRow({ onSave, onCancel, saveLabel = 'Save' }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <button className="btn-primary" style={{ flex: 1, marginBottom: 0 }} onClick={onSave}>{saveLabel}</button>
      {onCancel && <button className="btn-secondary" style={{ flex: 1, marginBottom: 0 }} onClick={onCancel}>Cancel</button>}
    </div>
  );
}
